/**
 * Liveblocks-backed canvas state for React.
 *
 * Wraps the canvas in a Liveblocks RoomProvider
 * (room id = `Canvas.liveblocks_room_id` from the API) and exposes nodes/edges/mutations
 * via `useCanvasLoro()`.
 *
 * NOTE: The hook is still exported as `useCanvasLoro` and the interface as
 * `CanvasContextType` for backward compatibility with the ~30 hook/component
 * files that import them from this module.
 */

import "../../../lib/liveblocks/liveblocks.config";

import { useCanvasById } from "@/api/canvas";
import { getApiErrorMessage } from "@/api/utils";
import { ErrorDisplay } from "@/components/ErrorBoundary/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { CANVAS_INITIAL_PRESENCE } from "@/lib/liveblocks/canvasPresence";
import type { Canvas } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { LiveObject } from "@liveblocks/client";
import {
  ClientSideSuspense,
  RoomProvider,
  useHistory,
  useMutation,
  useStatus,
} from "@liveblocks/react";
import { useLiveblocksFlow } from "@liveblocks/react-flow";
import type {
  Edge,
  Node,
  OnConnect,
  OnDelete,
  OnEdgesChange,
  OnNodesChange,
} from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useCanvasPermissions } from "../hooks/useCanvasPermissions";
import { connectorDataWithLocalEndpoints } from "../types/canvasNodeData";
import { SectionParentUpdate } from "../utils/sectionMembership";
import { CanvasRoomConnectionErrors } from "./CanvasRoomConnectionErrors";
import { useCanvasRoute } from "./CanvasRouteContext";

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/** Value supplied through the canvas context. */
export interface CanvasContextType {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  canEdit: boolean;
  canvas: Canvas | null;
  nodes: Node[];
  edges: Edge[];
  /** React Flow change handler — pass as `onNodesChange` to `<ReactFlow>`. */
  onNodesChange: OnNodesChange<Node>;
  onEdgesChange: OnEdgesChange<Edge>;
  /** Adds an edge when handles are connected. */
  onConnect: OnConnect;
  /** Called when nodes/edges are deleted via keyboard. */
  onDelete: OnDelete<Node, Edge>;
  /** No-op: Liveblocks handles remote updates automatically. */
  importRemoteUpdate: (update: Uint8Array) => void;
  addNode: (node: Node) => void;
  removeNode: (nodeId: string) => void;
  removeNodes: (nodeIds: string[]) => void;
  updateNodePosition: (
    nodeId: string,
    position: { x: number; y: number },
  ) => void;
  updateNodePositions: (
    updates: Array<{ id: string; position: { x: number; y: number } }>,
  ) => void;
  updateNodeData: (nodeId: string, data: Record<string, unknown>) => void;
  /** Wholesale-replace the entire `data` subtree of a node. Unlike `updateNodeData`
   * (which merges), this drops all existing keys and writes the provided object.
   * Used to finalize loading nodes: writing the real entity naturally drops
   * the `loading` flag without needing a separate delete step. */
  replaceNodeData: (nodeId: string, data: Record<string, unknown>) => void;
  updateNodeDimensions: (
    nodeId: string,
    dimensions: { width?: number; height?: number },
  ) => void;
  updateNodeZIndex: (nodeId: string, zIndex: number) => void;
  /** Hide/show a node on the canvas surface (React Flow's native `hidden`).
   * Hiding is the non-destructive counterpart to delete: the node — and, for a
   * file entity, the file's canvas membership — stays intact; the canvas just
   * stops rendering it. The rail lists hidden nodes with a "Show on canvas"
   * affordance. Persisted to Liveblocks like any other node field. */
  setNodeHidden: (nodeId: string, hidden: boolean) => void;
  /** Atomically update a node's parentId and position. Pass null to detach. */
  updateNodeParent: (
    nodeId: string,
    parentId: string | null,
    newPosition: { x: number; y: number },
  ) => void;
  /** Batch update parentId + position for N nodes in one commit. */
  batchUpdateNodeParents: (
    updates: Array<{
      nodeId: string;
      parentId: string | null;
      position: { x: number; y: number };
    }>,
  ) => void;
  /** Atomically update connector endpoints + bounding box. */
  updateConnectorEndpoints: (
    nodeId: string,
    endpoints: {
      start: { x: number; y: number };
      end: { x: number; y: number };
    },
    position: { x: number; y: number },
    dimensions: { width: number; height: number },
  ) => void;
  addEdge: (edge: {
    id: string;
    source: string;
    target: string;
    type?: string;
    sourceHandle?: string;
    targetHandle?: string;
    data?: Record<string, unknown>;
    // Native React Flow visual fields — the source of truth for edge styling
    // (data carries only curve_type).
    style?: Edge["style"];
    markerStart?: Edge["markerStart"];
    markerEnd?: Edge["markerEnd"];
    label?: Edge["label"];
  }) => void;
  removeEdge: (edgeId: string) => void;
  removeEdges: (edgeIds: string[]) => void;
  /** Set (or clear, when blank) an edge's native `label`, preserving its style. */
  updateEdgeLabel: (edgeId: string, label: string) => void;
  /**
   * Run a callback whose storage mutations should NOT enter the undo/redo
   * stack. Use for deletions tied to irreversible side effects (e.g. removing
   * a canvas node alongside deleting the underlying file record) — bringing
   * such a node back via undo would resurrect a phantom pointing at a
   * now-missing resource.
   *
   * Thin wrapper around `room.history.disable(fn)`. Returns the callback's
   * return value; if it throws, history stacks are left unchanged.
   */
  withoutUndo: <T>(fn: () => T) => T;
}

/** Volatile document slice — nodes/edges and React Flow change handlers (~60Hz during drag). */
export type CanvasDocContextType = Pick<
  CanvasContextType,
  "nodes" | "edges" | "onNodesChange" | "onEdgesChange" | "onConnect"
>;

/** Stable mutations and metadata — safe for memoized node components. */
export type CanvasApiContextType = Omit<
  CanvasContextType,
  keyof CanvasDocContextType
>;

// ---------------------------------------------------------------------------
// Context + hooks
// ---------------------------------------------------------------------------

const CanvasApiContext = createContext<CanvasApiContextType | null>(null);
const CanvasDocContext = createContext<CanvasDocContextType | null>(null);

/** Raw API context `Provider` — prefer `CanvasLiveblocksProvider`; use in tests to inject API only. */
export const CanvasApiContextProvider = CanvasApiContext.Provider;

/** Raw doc context `Provider` — prefer `CanvasLiveblocksProvider`; use in tests to inject doc only. */
export const CanvasDocContextProvider = CanvasDocContext.Provider;

/** @deprecated Prefer `CanvasApiContextProvider` + `CanvasDocContextProvider`. */
export const CanvasContextProvider = CanvasApiContextProvider;

/** Stable canvas API (mutations, permissions) without subscribing to nodes/edges. */
export function useCanvasApi(): CanvasApiContextType {
  const ctx = useContext(CanvasApiContext);
  if (!ctx) {
    throw new Error(
      "useCanvasApi must be used within CanvasLiveblocksProvider",
    );
  }
  return ctx;
}

/** Liveblocks nodes/edges — re-renders on every document tick. */
export function useCanvasDoc(): CanvasDocContextType {
  const ctx = useContext(CanvasDocContext);
  if (!ctx) {
    throw new Error(
      "useCanvasDoc must be used within CanvasLiveblocksProvider",
    );
  }
  return ctx;
}

/** Full canvas context (API + document). Use in CanvasPage and drag hooks that need live nodes. */
export function useCanvas(): CanvasContextType {
  const api = useCanvasApi();
  const doc = useCanvasDoc();
  return useMemo(() => ({ ...api, ...doc }), [api, doc]);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Order nodes for React Flow on every Liveblocks sync.
 *
 * Two constraints (parent-first alone does not satisfy both):
 * 1. Each `parentId` target must appear before its children so
 *    `adoptUserNodes` can resolve absolute positions (nested sections).
 * 2. All `section` nodes before other types so section backdrops paint
 *    under free-floating entities.
 *
 * O(n): DFS parent-first pass, then stable section / non-section partition.
 */
function sortCanvasNodes(nodes: Node[]): Node[] {
  if (nodes.length < 2) return nodes;

  const byId = new Map<string, Node>();
  for (const node of nodes) byId.set(node.id, node);

  const parentFirst: Node[] = [];
  const visited = new Set<string>();

  const visit = (node: Node) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) visit(parent);
    }
    parentFirst.push(node);
  };

  for (const node of nodes) visit(node);

  let sectionCount = 0;
  for (const node of parentFirst) {
    if (node.type === "section") sectionCount++;
  }
  if (sectionCount === 0 || sectionCount === parentFirst.length) {
    return parentFirst;
  }

  const sections: Node[] = [];
  const rest: Node[] = [];
  for (const node of parentFirst) {
    if (node.type === "section") sections.push(node);
    else rest.push(node);
  }
  return sections.concat(rest);
}

// ---------------------------------------------------------------------------
// Inner hook — must run inside a Liveblocks RoomProvider
// ---------------------------------------------------------------------------

function useCanvasLiveblocksDoc(canvasId: string): {
  api: CanvasApiContextType;
  doc: CanvasDocContextType;
} {
  const { data: canvas = null } = useCanvasById(canvasId);
  const { canEdit } = useCanvasPermissions();

  const {
    nodes: rawNodes,
    edges: rawEdges,
    onNodesChange: liveblocksOnNodesChange,
    onEdgesChange: liveblocksOnEdgesChange,
    onConnect,
    onDelete,
    isLoading,
  } = useLiveblocksFlow<Node, Edge>({ suspense: false });
  const { setNodes } = useReactFlow<Node>();

  // React Flow REQUIRES parents to appear before children in the nodes
  // array — otherwise `adoptUserNodes`/`updateChildNode` can't find the
  // parent in `nodeLookup`, child positions are never offset by the
  // parent's absolute position (children render at raw relative coords,
  // i.e. jump to top-left), and the child is never added to
  // `parentLookup` (so parent drags don't propagate to children).
  //
  // Liveblocks's `useLiveblocksFlow` returns nodes via
  // `Object.values(nodesMap)` — i.e. LiveMap insertion order — which
  // does NOT reflect parent-child order (a section drawn over existing
  // nodes is inserted AFTER the nodes it adopts).
  //
  const nodes = useMemo<Node[]>(
    () => sortCanvasNodes(rawNodes ?? []),
    [rawNodes],
  );
  const edges = (rawEdges ?? []) as Edge[];
  const onNodesChange = liveblocksOnNodesChange as OnNodesChange<Node>;
  const onEdgesChange = liveblocksOnEdgesChange as OnEdgesChange<Edge>;

  // Keep stable refs so mutation callbacks read the latest arrays.
  const nodesRef = useRef<Node[]>(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef<Edge[]>(edges);
  edgesRef.current = edges;

  // ------------------------------------------------------------------
  // Mutation helpers — implemented via onNodesChange / onEdgesChange
  // ------------------------------------------------------------------

  const addNode = useCallback(
    (node: Node) => onNodesChange([{ type: "add", item: node }]),
    [onNodesChange],
  );

  // `@liveblocks/react-flow` ignores `"remove"` NodeChange/EdgeChange
  // entries entirely (its internal applyNodeChanges has `case "remove":
  // break;`). Deletes are persisted only through the separate `onDelete`
  // mutation — the same one we wire to `<ReactFlow onDelete={...}>`.
  // We reuse it for every programmatic removal path so trash buttons,
  // keyboard shortcuts (which preventDefault React Flow's built-in
  // handler), and section deletion all actually delete from storage.
  //
  const removeNode = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      onDelete({
        nodes: [node],
        edges: [],
      });
    },
    [onDelete],
  );

  const removeNodes = useCallback(
    (nodeIds: string[]) => {
      if (nodeIds.length === 0) return;
      const idSet = new Set(nodeIds);
      const toDelete = nodesRef.current.filter((n) => idSet.has(n.id));
      if (toDelete.length === 0) return;
      onDelete({
        nodes: toDelete,
        edges: [],
      });
    },
    [onDelete],
  );

  const addEdge = useCallback(
    (edge: {
      id: string;
      source: string;
      target: string;
      type?: string;
      sourceHandle?: string;
      targetHandle?: string;
      data?: Record<string, unknown>;
      style?: Edge["style"];
      markerStart?: Edge["markerStart"];
      markerEnd?: Edge["markerEnd"];
      label?: Edge["label"];
    }) => onEdgesChange([{ type: "add", item: edge as Edge }]),
    [onEdgesChange],
  );

  const removeEdge = useCallback(
    (edgeId: string) => {
      const edge = edgesRef.current.find((e) => e.id === edgeId);
      if (!edge) return;
      onDelete({
        nodes: [],
        edges: [edge],
      });
    },
    [onDelete],
  );

  // Set/clear an edge's native `label`, preserving its other native fields.
  const updateEdgeLabel = useCallback(
    (edgeId: string, label: string) => {
      const e = edgesRef.current.find((x) => x.id === edgeId);
      if (!e) return;
      const next = label.trim() || undefined;
      addEdge({
        id: edgeId,
        source: e.source,
        target: e.target,
        type: e.type,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        style: e.style,
        markerStart: e.markerStart,
        markerEnd: e.markerEnd,
        data: e.data as Record<string, unknown>,
        label: next,
      });
    },
    [addEdge],
  );

  const removeEdges = useCallback(
    (edgeIds: string[]) => {
      if (edgeIds.length === 0) return;
      const idSet = new Set(edgeIds);
      const toDelete = edgesRef.current.filter((e) => idSet.has(e.id));
      if (toDelete.length === 0) return;
      onDelete({
        nodes: [],
        edges: toDelete,
      });
    },
    [onDelete],
  );

  // Position/dimension updates from React Flow's interactive paths (drag,
  // resize handle) already round-trip through liveblocksOnNodesChange — we
  // don't need to do anything for those. But programmatic callers (section
  // collapse, resize-to-fit, snap-to-grid, etc.) bypass React Flow entirely
  // and must dispatch their own NodeChange entries to land in storage.
  //
  // We use the canonical change types (`position`, `dimensions`) instead of
  // a `replace` so Liveblocks's atomic field setters run and we don't clobber
  // unrelated local-only state (selection, dragging, measured).
  //
  // For dimensions, `setAttributes` controls which fields actually persist;
  // we map the partial input shape to the appropriate value so callers can
  // update width-only or height-only without zeroing the other.
  const updateNodePosition = useCallback(
    (id: string, position: { x: number; y: number }) =>
      onNodesChange([{ type: "position", id, position }]),
    [onNodesChange],
  );
  const updateNodePositions = useCallback(
    (updates: Array<{ id: string; position: { x: number; y: number } }>) => {
      if (updates.length === 0) return;
      onNodesChange(
        updates.map(({ id, position }) => ({
          type: "position" as const,
          id,
          position,
        })),
      );
    },
    [onNodesChange],
  );
  const updateNodeDimensions = useCallback(
    (id: string, dims: { width?: number; height?: number }) => {
      const hasW = dims.width !== undefined;
      const hasH = dims.height !== undefined;
      if (!hasW && !hasH) return;
      const setAttributes: true | "width" | "height" =
        hasW && hasH ? true : hasW ? "width" : "height";
      onNodesChange([
        {
          type: "dimensions",
          id,
          dimensions: { width: dims.width ?? 0, height: dims.height ?? 0 },
          setAttributes,
        },
      ]);
    },
    [onNodesChange],
  );

  // ⚠️  `replaceNode` reads the node from `nodesRef.current`, which only
  // refreshes when React re-renders. Two synchronous calls in the same
  // tick both see the *same* stale node. The dispatched "replace" change
  // triggers a Liveblocks `reconcile` with extent: "full" — meaning any
  // key on the LiveObject that isn't present in the new item is DELETED.
  // Net effect: call N+1 silently clobbers fields written by call N.
  //
  // Safe to use for: single-call-per-action mutations (z-index toggle,
  // section auto-organize commit, connector endpoints update).
  // UNSAFE for: anything that may fire back-to-back with a sibling update
  // touching different keys (e.g., line-style + line-width pickers).
  // For those, use a `useMutation`-backed primitive that writes against
  // live storage instead — see `updateNodeData` below.
  const replaceNode = useCallback(
    (updater: (node: Node) => Node, nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      onNodesChange([{ type: "replace", id: nodeId, item: updater(node) }]);
    },
    [onNodesChange],
  );

  // Atomic per-key updates against the LiveObject's `data` field. Reads
  // live storage (not a React ref), so consecutive calls in the same tick
  // compose correctly — call N's writes are visible to call N+1, and
  // neither clobbers keys set by the other.
  //
  // This is a deliberate departure from the `replaceNode` pattern: that
  // pattern dispatches a "replace" NodeChange and lets Liveblocks's full
  // reconcile rebuild the data sub-object, which loses any keys missing
  // from the (stale) snapshot. Shape floating toolbars fire two
  // `updateNodeData` calls back-to-back
  // (style + width) and were silently clobbering each other under the
  // old implementation.
  //
  // The `data` field is created as a LiveObject by Liveblocks's
  // `toLiveblocksInternalNode` (its sync config has no entry for `data`,
  // so `deepLiveify` defaults to liveifying plain objects). We branch on
  // `instanceof LiveObject` defensively — if a future schema change ever
  // makes `data` an atomic JSON blob, we fall back to whole-object
  // replacement so the call still does the right thing (with the
  // staleness caveat reintroduced for that branch only).
  const updateNodeData = useMutation(
    ({ storage }, nodeId: string, patch: Record<string, unknown>) => {
      // Storage shape is owned by `useLiveblocksFlow` and intentionally
      // not declared in the global Liveblocks types (see
      // liveblocks.config.ts). We escape the type system here — both at
      // the storage key (`"flow"`) and on the LiveObject returns — and
      // rely on runtime `instanceof LiveObject` for the only branch that
      // matters for correctness.
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      const node = nodes?.get?.(nodeId) as
        | {
            get(key: string): unknown;
            set(key: string, value: unknown): void;
          }
        | undefined;
      if (!node) return;
      const data = node.get("data");
      if (data instanceof LiveObject) {
        // Dedupe primitive equals (`===`). Without this, callers that fire
        // on `OnChange`-style listeners — Lexical's `OnChangePlugin` runs on
        // any editor-state change including selection-only — push storage
        // writes for keys whose value didn't actually change. Each write
        // bumps the undo stack AND wipes the redo stack, so e.g. clicking
        // into a text node was destroying the user's redo history.
        //
        // We compare with `===` only: nested objects/arrays in `data` come
        // out of storage as `LiveObject` / `LiveList`, and incoming patch
        // values are plain JS — `===` is always false there, so they fall
        // through to a write (the safe default). This catches the common
        // case (booleans, numbers, color/font strings) which is enough.
        const liveData = data as unknown as {
          get(key: string): unknown;
          set(key: string, value: unknown): void;
        };
        for (const [key, value] of Object.entries(patch)) {
          if (liveData.get(key) === value) continue;
          liveData.set(key, value);
        }
        return;
      }
      // Fallback: `data` is an atomic JSON blob (not a LiveObject). The
      // staleness footgun documented above on `replaceNode` applies here —
      // we accept it because this branch is reachable only via an explicit
      // schema choice that opts out of LiveObject sync.
      node.set("data", {
        ...((data as Record<string, unknown> | undefined) ?? {}),
        ...patch,
      });
    },
    [],
  );

  const replaceNodeData = useMutation(
    ({ storage }, nodeId: string, newData: Record<string, unknown>) => {
      const flow = storage.get("flow" as never) as
        | { get(key: string): unknown }
        | undefined;
      if (!flow || typeof flow.get !== "function") return;
      const nodes = flow.get("nodes") as
        | { get(id: string): unknown }
        | undefined;
      const node = nodes?.get?.(nodeId) as
        | {
            get(key: string): unknown;
            set(key: string, value: unknown): void;
          }
        | undefined;
      if (!node) return;
      node.set("data", newData);
    },
    [],
  );

  const updateNodeZIndex = useCallback(
    (nodeId: string, zIndex: number) =>
      replaceNode((n) => ({ ...n, zIndex }), nodeId),
    [replaceNode],
  );

  const setNodeHidden = useCallback(
    (nodeId: string, hidden: boolean) =>
      replaceNode((n) => ({ ...n, hidden }), nodeId),
    [replaceNode],
  );

  const updateNodeParent = useCallback(
    (
      nodeId: string,
      parentId: string | null,
      newPosition: { x: number; y: number },
    ) =>
      replaceNode(
        (n) => ({
          ...n,
          parentId: parentId ?? undefined,
          position: newPosition,
        }),
        nodeId,
      ),
    [replaceNode],
  );

  const batchUpdateNodeParents = useCallback(
    (updates: SectionParentUpdate[]) => {
      if (updates.length === 0) return;
      setNodes((current) => {
        const byId = new Map(current.map((n) => [n.id, { ...n }]));
        for (const update of updates) {
          const node = byId.get(update.nodeId);
          if (!node) continue;
          const next: Node = {
            ...node,
            position: update.position,
            parentId: update.parentId ?? undefined,
          };
          byId.set(update.nodeId, next);
        }
        return sortCanvasNodes([...byId.values()]);
      });
    },
    [setNodes],
  );

  const updateConnectorEndpoints = useCallback(
    (
      nodeId: string,
      endpoints: {
        start: { x: number; y: number };
        end: { x: number; y: number };
      },
      position: { x: number; y: number },
      dimensions: { width: number; height: number },
    ) =>
      replaceNode(
        (n) => ({
          ...n,
          position,
          width: dimensions.width,
          height: dimensions.height,
          data: connectorDataWithLocalEndpoints(
            (n.data ?? {}) as Record<string, unknown>,
            endpoints.start,
            endpoints.end,
          ),
        }),
        nodeId,
      ),
    [replaceNode],
  );

  const importRemoteUpdate = useCallback((_: Uint8Array) => {
    // No-op: Liveblocks handles remote state automatically.
  }, []);

  // `useHistory()` returns the room's `History` object directly, so the
  // identity is stable across renders and `withoutUndo` doesn't need a
  // `useCallback` ref.
  const history = useHistory();
  const withoutUndo = useCallback(
    <T,>(fn: () => T): T => history.disable(fn),
    [history],
  );

  const api = useMemo<CanvasApiContextType>(
    () => ({
      isReady: !isLoading,
      isLoading: isLoading ?? false,
      error: null,
      canEdit,
      canvas,
      onDelete,
      importRemoteUpdate,
      addNode,
      removeNode,
      removeNodes,
      addEdge,
      removeEdge,
      removeEdges,
      updateEdgeLabel,
      updateNodePosition,
      updateNodePositions,
      updateNodeData,
      replaceNodeData,
      updateNodeDimensions,
      updateNodeZIndex,
      setNodeHidden,
      updateNodeParent,
      batchUpdateNodeParents,
      updateConnectorEndpoints,
      withoutUndo,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoading,
      canEdit,
      canvas,
      onDelete,
      importRemoteUpdate,
      addNode,
      removeNode,
      removeNodes,
      addEdge,
      removeEdge,
      removeEdges,
      updateEdgeLabel,
      updateNodePosition,
      updateNodePositions,
      updateNodeData,
      replaceNodeData,
      updateNodeDimensions,
      updateNodeZIndex,
      setNodeHidden,
      updateNodeParent,
      batchUpdateNodeParents,
      updateConnectorEndpoints,
      withoutUndo,
    ],
  );

  const doc = useMemo<CanvasDocContextType>(
    () => ({
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onConnect,
    }),
    [nodes, edges, onNodesChange, onEdgesChange, onConnect],
  );

  return { api, doc };
}

// ---------------------------------------------------------------------------
// Inner component (inside RoomProvider, can call useLiveblocksFlow)
// ---------------------------------------------------------------------------

function Loader() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-background">
      <Spinner size="lg" />
    </div>
  );
}

function CanvasLiveblocksInner({
  canvasId,
  children,
}: {
  canvasId: string;
  children: ReactNode;
}) {
  const { api, doc } = useCanvasLiveblocksDoc(canvasId);
  return (
    <CanvasApiContext.Provider value={api}>
      <CanvasDocContext.Provider value={doc}>
        {children}
      </CanvasDocContext.Provider>
    </CanvasApiContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Public provider
// ---------------------------------------------------------------------------

/**
 * Wraps the canvas route in a Liveblocks room and provides `useCanvasLoro()`
 * to all descendants. Replaces the old `CanvasLoroProvider`.
 */
export function CanvasLiveblocksProvider({
  children,
  uiVariant = "interactive",
}: {
  children: ReactNode;
  /** Thumbnail / headless capture skips connection error overlay. */
  uiVariant?: "interactive" | "capture" | "view";
}) {
  // No Auth0 dependency here: `useCanvasById` calls `orgAPI.canvas.getById`,
  // which derives org/user from whichever bearer token `orgAPI` has been
  // pointed at. In the main app that's Auth0 (`AuthInject`); in the
  // canvas-capture entry it's the short-lived capture JWT
  // (`CaptureOnlyApiInject`). Gating this provider on an Auth0-derived
  // `orgId` used to wedge the capture page on `<Loader />` forever because
  // the capture entry has no `Auth0Provider` — leave the api-client
  // interceptor to handle token readiness.
  const { canvasId } = useCanvasRoute();
  const showConnectionErrorOverlay = uiVariant !== "capture";

  const canvasQuery = useCanvasById(canvasId);

  if (!canvasId) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="No canvas selected"
          message="Open a canvas from your workspace or the sidebar to view it here."
        />
      </div>
    );
  }

  if (canvasQuery.isPending) {
    return <Loader />;
  }

  if (canvasQuery.isError) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="Could not load canvas"
          message={getApiErrorMessage(canvasQuery.error)}
          footer={
            <Button type="button" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          }
        />
      </div>
    );
  }

  const canvas = canvasQuery.data;
  const roomId = canvas?.liveblocks_room_id?.trim() ?? "";
  if (!canvas || !roomId) {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="Canvas unavailable"
          message="This canvas is missing collaboration room information. Try refreshing the page."
          footer={
            <Button type="button" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <RoomProvider id={roomId} initialPresence={CANVAS_INITIAL_PRESENCE}>
      <CanvasRoomConnectionErrors
        roomId={roomId}
        showOverlay={showConnectionErrorOverlay}
      />
      <ClientSideSuspense fallback={<Loader />}>
        <CanvasLiveBlocksRoomLoading>
          <CanvasLiveblocksInner canvasId={canvasId}>
            {children}
          </CanvasLiveblocksInner>
        </CanvasLiveBlocksRoomLoading>
      </ClientSideSuspense>
    </RoomProvider>
  );
}

// Shows a loading spinner while the room is loading (websocket connection)
// or the room is initializing (first time connecting).
export function CanvasLiveBlocksRoomLoading({
  children,
}: {
  children: ReactNode;
}) {
  const status = useStatus();

  if (status === "connecting" || status === "initial") {
    return <Loader />;
  }

  return children;
}
